

















































































































































































































































import { Component, Watch, Prop, Mixins } from 'vue-property-decorator';
import { UserHelper } from '@/helper/user';
import { LoadingIndicator } from '@/components/loading-indicator/LoadingIndicator.tsx';
import { ShoppingCart } from '@/entities/ShoppingCart';
import BaseMixin from '@/mixins/BaseMixin';
import { ShoppingCartModuleTypes } from '@/store/modules/shopping-cart';
import ShoppingCartUploadResultTable from '@/components/shopping-cart/upload/ShoppingCartUploadResultTable.vue';
import { ShoppingCartUploadResultType } from '@/entities/shopping-cart-upload/ShoppingCartUploadResultType';
import { ShoppingCartUploadModuleTypes } from '../../../store/modules/shopping-cart-upload';
import { ShoppingCartUploadResult } from '../../../entities/shopping-cart-upload/ShoppingCartUploadResult';
import { LoadingState } from '../../../entities/LoadingState';
import Price from '../../price/Price.vue';
import { ShoppingCartUploadInfo } from '@/entities/shopping-cart-upload/ShoppingCartUploadInfo';
import { ShoppingCartUploadProcessingState } from '@/entities/shopping-cart-upload/ShoppingCartUploadProcessingState';
import { localize } from '@/filters/localize';

@Component({
    components: {
        LoadingIndicator,
        ShoppingCartUploadResultTable,
    },
})
export default class ShoppingCartUpload extends Mixins(BaseMixin) {
    private selectedShoppingCartId: number | undefined;
    private ResultTypes: typeof ShoppingCartUploadResultType = ShoppingCartUploadResultType;
    private LoadingStates: typeof LoadingState = LoadingState;
    private refreshUploadInfoMilliseconds: number = 1000;
    private searchForUsedParts: boolean = false;

    public get isUserAuthenticated(): boolean {
        return UserHelper.isAuthenticated();
    }

    public get shoppingCarts(): ShoppingCart[] {
        return this.$store.getters[ShoppingCartModuleTypes.getters.shoppingCarts];
    }

    public get loadingState(): LoadingState {
        return this.$store.getters[ShoppingCartUploadModuleTypes.getters.loadingState];
    }

    public get resultFromUpload(): ShoppingCartUploadResult | undefined {
        return this.uploadInfo && this.uploadInfo.result;
    }

    public get processedInPercent(): string | undefined {
        return this.uploadInfo != null
            ? this.uploadInfo.processedInPercent.toString()
            : localize(this.l10n.SHOPPING_CART_UPLOAD.PROGRESS_UNDEFINED);
    }

    public get uploadInfo(): ShoppingCartUploadInfo | undefined {
        return this.$store.getters[ShoppingCartUploadModuleTypes.getters.uploadInfo];
    }

    public get showErrorText(): boolean {
        return this.uploadInfo != null && this.uploadInfo.state === ShoppingCartUploadProcessingState.Error;
    }

    public get totalLinesParsed(): number {
        return this.resultFromUpload !== undefined
            ? this.resultFromUpload.foundParts.length +
                  this.resultFromUpload.replacementParts.length +
                  this.resultFromUpload.notFoundParts.length
            : 0;
    }

    public get selectedPartIds(): string[] {
        return this.$store.getters[ShoppingCartUploadModuleTypes.getters.selectedPartIds];
    }

    public get totalAmountOfSelectedItems(): Map<string, number> {
        if (!this.resultFromUpload) {
            return new Map();
        }

        const sumCents = new Map<string, number>();
        this.resultFromUpload.foundParts
            .filter((part) => this.selectedPartIds.includes(part.partId!))
            .concat(
                this.resultFromUpload.replacementParts.filter((part) =>
                    this.selectedPartIds.includes(part.replacementPartId!)
                )
            )
            .concat(this.resultFromUpload.usedParts.filter((part) => this.selectedPartIds.includes(part.partId!)))
            .forEach((part) => {
                const price = new Price({
                    propsData: {
                        priceNetCents: part.price!.priceNetCents * part.quantity,
                        priceGrossCents: part.price!.priceGrossCents * part.quantity,
                        priceDiscountedNetCents: part.price!.priceDiscountedNetCents * part.quantity,
                    },
                });
                let sumCentsForCurrency = sumCents.has(part.price!.currency.htmlSymbol) ? sumCents.get(part.price!.currency.htmlSymbol)! : 0;
                sumCentsForCurrency += price.bigFontPrice;
                sumCents.set(part.price!.currency.htmlSymbol, sumCentsForCurrency);
            });
        return sumCents;
    }

    // called by vue
    protected created(): void {
        this.refreshUploadState();
    }

    @Watch('shoppingCarts')
    private setFirstValueInShoppingCartSelect(): void {
        if (this.shoppingCarts.length > 0) {
            this.selectedShoppingCartId = this.shoppingCarts[0].id;
        }
    }

    private openFileDialog(): void {
        const fileInput = this.$refs.fileInput as HTMLInputElement;
        if (fileInput) {
            fileInput.click();
        }
    }

    private excelOrCsvFileSelected(): void {
        const fileInput = this.$refs.fileInput as HTMLInputElement;
        if (fileInput && fileInput.files && fileInput.files.length > 0) {
            const selectedFile = fileInput.files[0];
            this.$store.dispatch(ShoppingCartUploadModuleTypes.actions.uploadFileAndStartSearch, {
                file: selectedFile,
                includeUsedParts: this.searchForUsedParts,
            });
        }

        this.refreshUploadInfoMilliseconds = 1000;
        this.startTimer();
    }

    private startTimer(): void {
        setTimeout(this.refreshUploadState, this.refreshUploadInfoMilliseconds);
        if (this.refreshUploadInfoMilliseconds < 3000) {
            this.refreshUploadInfoMilliseconds = this.refreshUploadInfoMilliseconds + 1000;
        }
    }

    private async refreshUploadState(): Promise<void> {
        await this.$store.dispatch(ShoppingCartUploadModuleTypes.actions.updateCurrentUploadInfo);
        if (this.uploadInfo && this.uploadInfo.state === ShoppingCartUploadProcessingState.Running) {
            this.startTimer();
        }
    }

    private async addSelectedPartsToCart(intoNewCart: boolean): Promise<void> {
        if (!this.resultFromUpload || this.selectedPartIds.length === 0) {
            return;
        }

        this.$store.commit(ShoppingCartUploadModuleTypes.mutations.updateLoadingState, {
            loadingState: LoadingState.Loading,
        });
        if (!intoNewCart) {
            await this.$store.dispatch(ShoppingCartModuleTypes.actions.setActiveShoppingCart, {
                shoppingCartId: this.selectedShoppingCartId,
            });
        }
        const selectedPartIds = this.$store.getters[ShoppingCartUploadModuleTypes.getters.selectedPartIds] as string[];
        const partsToAdd = new Array<{ partId: string; quantity: number; userInfoText: string }>();
        this.resultFromUpload.foundParts
            .filter((part) => selectedPartIds.includes(part.partId!))
            .filter((part) => part.quantity > 0)
            .forEach((part) =>
                partsToAdd.push({ partId: part.partId!, quantity: part.quantity, userInfoText: part.infoText })
            );
        this.resultFromUpload.replacementParts
            .filter((part) => selectedPartIds.includes(part.replacementPartId!))
            .filter((part) => part.quantity > 0)
            .forEach((part) =>
                partsToAdd.push({
                    partId: part.replacementPartId!,
                    quantity: part.quantity,
                    userInfoText: part.infoText,
                })
            );
        this.resultFromUpload.usedParts
            .filter((part) => selectedPartIds.includes(part.partId!))
            .filter(
                (part) =>
                    part.quantity > 0 &&
                    part.partAvailability &&
                    part.quantity <=
                        part.partAvailability.stockInfos
                            .map((info) => info.quantity)
                            .reduce((previousValue, currentValue) => previousValue + currentValue, 0)
            )
            .forEach((part) =>
                partsToAdd.push({ partId: part.partId!, quantity: part.quantity, userInfoText: part.infoText })
            );
        await this.$store.dispatch(ShoppingCartModuleTypes.actions.addShoppingCartItemsInBulk, {
            items: partsToAdd,
            cartId: intoNewCart ? undefined : this.selectedShoppingCartId,
        });
        this.$store.commit(ShoppingCartUploadModuleTypes.mutations.updateLoadingState, {
            loadingState: LoadingState.Loaded,
        });

        // redirect to cart overview
        window.location.href = '/Cart';
    }
}
